import React, { useState, useEffect, memo } from 'react';
import { Smartphone, SmartphoneCharging, X, Phone, MessageSquare } from 'lucide-react';
import { API_URL } from '../config';

// CSS animace pro efekty
const animations = `
  @keyframes borderLightning {
    0% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
    25% {
      box-shadow: 0 0 30px 5px rgba(239, 68, 68, 0.8);
    }
    50% {
      box-shadow: 0 0 40px 10px rgba(239, 68, 68, 1);
    }
    75% {
      box-shadow: 0 0 30px 5px rgba(239, 68, 68, 0.8);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

// Konstantní detekce — nikdy se nemění, žádný state
const IS_MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  navigator.userAgent || ''
);

const PhoneSync = ({ userId, currentCompanyId, hideIcon = false }) => {
  const [syncStatus, setSyncStatus] = useState({ hasBothDevices: false, syncEnabled: false });
  const [isLoading, setIsLoading] = useState(true);
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [showMobilePairing, setShowMobilePairing] = useState(false);
  const [mobileCode, setMobileCode] = useState('');
  const [currentCompany, setCurrentCompany] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [deviceId] = useState(() => {
    let id = localStorage.getItem('deviceId');
    if (!id) {
      id = `${IS_MOBILE ? 'mob' : 'pc'}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', id);
    }
    return id;
  });

  // Registrace zařízení a heartbeat
  useEffect(() => {
    if (!userId) return;

    const registerDevice = () => fetch(`${API_URL}/device-sync/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, deviceId, deviceType: IS_MOBILE ? 'mobile' : 'pc' })
    }).catch(() => {});

    registerDevice();
    const t = setInterval(registerDevice, 30000);
    return () => clearInterval(t);
  }, [userId, deviceId]);

  // Načítání statusu synchronizace
  useEffect(() => {
    if (!userId) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/device-sync/status?userId=${userId}`);
        if (response.ok) {
          const data = await response.json();
          setSyncStatus(prev =>
            prev.hasBothDevices === data.hasBothDevices && prev.syncEnabled === data.syncEnabled
              ? prev : { hasBothDevices: data.hasBothDevices, syncEnabled: data.syncEnabled }
          );
        }
      } catch (error) {
        console.error('Chyba při načítání statusu:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 10000); // 10s - status se nemění často
    
    return () => clearInterval(interval);
  }, [userId]);

  // PC: zapsat firmu na backend (pouze pokud je sync zapnutý)
  useEffect(() => {
    if (!userId || IS_MOBILE || !syncStatus.syncEnabled) return;
    fetch(`${API_URL}/device-sync/set-company`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, deviceId, companyId: currentCompanyId || null })
    })
      .catch(() => {});
  }, [userId, deviceId, currentCompanyId, syncStatus.syncEnabled]);

  // Mobil: poll firmy z PC každé 3s (pouze pokud je sync zapnutý)
  useEffect(() => {
    if (!userId || !IS_MOBILE || !syncStatus.syncEnabled) {
      setCurrentCompany(null);
      return;
    }
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/device-sync/current-company?userId=${userId}`);
        if (res.ok) {
          const data = await res.json();
            setCurrentCompany(prev => {
            const newId = data.company?._id ?? null;
            const prevId = prev?._id ?? null;
            if (newId === prevId) return prev;
            return data.company ?? null;
          });
        }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 3000);
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [userId, syncStatus.syncEnabled]);

  // 
  // Generování párovacího kódu
  const generatePairingCode = async () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setPairingCode(code);
    
    console.log('🔑 Generuji párovací kód:', code, 'pro userId:', userId, 'deviceId:', deviceId);
    
    // Ulož kód do backendu
    try {
      const response = await fetch(`${API_URL}/device-sync/set-pairing-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          deviceId,
          code
        })
      });
      
      const result = await response.json();
      console.log('✅ Kód uložen na backend:', result);
    } catch (error) {
      console.error('❌ Chyba při ukládání párovacího kódu:', error);
    }
  };

  // Ověření párovacího kódu na mobilu
  const verifyMobileCode = async () => {
    if (!mobileCode || mobileCode.length !== 6) {
      alert('⚠️ Zadejte 6-místný kód');
      return;
    }
    
    console.log('🔍 Ověřuji párovací kód:', mobileCode, 'pro userId:', userId, 'deviceId:', deviceId);
    
    try {
      const response = await fetch(`${API_URL}/device-sync/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          deviceId,
          code: mobileCode
        })
      });

      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Response data:', data);
        
        if (data.success) {
          setShowMobilePairing(false);
          setMobileCode('');
          setSyncStatus({ hasBothDevices: true, syncEnabled: true });
          alert('✓ Telefon spárován! Synchronizace aktivní.');
          
          // Načti aktuální status z backendu
          setTimeout(async () => {
            try {
              const statusResponse = await fetch(`${API_URL}/device-sync/status?userId=${userId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                setSyncStatus(statusData);
                console.log('✅ Status aktualizován:', statusData);
              }
            } catch (error) {
              console.error('Chyba při načítání statusu:', error);
            }
          }, 500);
        } else {
          alert('✗ Neplatný kód nebo PC není aktivní\n' + (data.error || ''));
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        alert('✗ Chyba serveru: ' + response.status);
      }
    } catch (error) {
      console.error('❌ Chyba při ověřování kódu:', error);
      alert('✗ Chyba při párování: ' + error.message);
    }
  };

  const toggleSync = async () => {
    if (IS_MOBILE && !syncStatus.syncEnabled) { setShowMobilePairing(true); return; }
    if (!IS_MOBILE && (!syncStatus.syncEnabled || !syncStatus.hasBothDevices)) {
      await generatePairingCode();
      setShowPairingModal(true);
      return;
    }
    if (syncStatus.syncEnabled) setShowDisconnectConfirm(true);
  };

  // Potvrzení odpojení
  const confirmDisconnect = async () => {
    try {
      const response = await fetch(`${API_URL}/device-sync/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          deviceId,
          syncEnabled: false
        })
      });

      if (response.ok) {
        // Vymaž aktuální firmu na backendu při odpojení
        await fetch(`${API_URL}/device-sync/set-company`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, deviceId, companyId: null })
        }).catch(() => {});
        setSyncStatus({ hasBothDevices: false, syncEnabled: false });
        setCurrentCompany(null);
        setShowDisconnectConfirm(false);
      }
    } catch (error) {
      console.error('Chyba při odpojování:', error);
    }
  };

  if (isLoading || !userId) return null;

  const isSynced = syncStatus.syncEnabled && syncStatus.hasBothDevices;

  return (
    <>
      <style>{animations}</style>

      {!hideIcon && (
      <div className="relative">
        <button
          onClick={toggleSync}
          className={`fixed group p-3 rounded-full shadow-lg transition-all duration-300 ${
            isSynced
              ? 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700'
              : syncStatus.hasBothDevices
              ? 'bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700'
              : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
          }`}
          style={{ 
            zIndex: 10000,
            position: 'fixed',
            ...(IS_MOBILE
              ? { bottom: 'calc(20px + env(safe-area-inset-bottom))', left: '20px' }
              : { bottom: '44px', right: '182px' })
          }}
          title={IS_MOBILE ? 'Spárovat s PC' : isSynced ? 'Vypnout synchronizaci' : 'Spárovat telefon'}
        >
          {isSynced ? (
            <SmartphoneCharging className="w-6 h-6 text-white animate-pulse" />
          ) : (
            <Smartphone className="w-6 h-6 text-white" />
          )}
          
          {isSynced && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full border-2 border-white dark:border-gray-900 animate-pulse flex items-center justify-center">
              <span className="text-white text-xs font-bold">✓</span>
            </div>
          )}
          
          {syncStatus.hasBothDevices && !isSynced && !IS_MOBILE && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-400 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
          )}
        </button>
      </div>
      )}

      {showPairingModal && !IS_MOBILE && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 15000 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                📱 Spárování s telefonem
              </h2>
              <button
                onClick={() => setShowPairingModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="text-center py-6">
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Otevřete CRM na telefonu a zadejte tento kód:
              </p>
              
              <div className="bg-blue-50 dark:bg-blue-900 border-2 border-blue-500 rounded-lg p-6 mb-6">
                <div className="text-5xl font-bold text-blue-600 dark:text-blue-300 tracking-wider font-mono">
                  {pairingCode}
                </div>
              </div>
              
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Kód je platný 5 minut
              </p>
              
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Jak to funguje:</strong>
                </p>
                <ol className="text-sm text-gray-600 dark:text-gray-300 text-left mt-2 space-y-1">
                  <li>1. Otevřete CRM na telefonu</li>
                  <li>2. Klikněte na ikonku 📱 vlevo nahoře</li>
                  <li>3. Zadejte výše uvedený kód</li>
                  <li>4. Zařízení se automaticky spárují</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMobilePairing && IS_MOBILE && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 15000 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                📱 Zadejte párovací kód
              </h2>
              <button
                onClick={() => {
                  setShowMobilePairing(false);
                  setMobileCode('');
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="text-center py-6">
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Zadejte 6-místný kód z PC:
              </p>
              
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mobileCode}
                onChange={(e) => setMobileCode(e.target.value.replace(/\D/g, ''))}
                className="w-full text-4xl font-bold text-center tracking-widest font-mono bg-blue-50 dark:bg-blue-900 border-2 border-blue-500 rounded-lg p-4 mb-6"
                placeholder="000000"
                autoFocus
              />
              
              <button
                onClick={verifyMobileCode}
                disabled={mobileCode.length !== 6}
                className="w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {mobileCode.length === 6 ? '✓ Spárovat' : `Zadejte ${6 - mobileCode.length} číslic`}
              </button>
              
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Postup:</strong>
                </p>
                <ol className="text-sm text-gray-600 dark:text-gray-300 text-left mt-2 space-y-1">
                  <li>1. Na PC klikněte na ikonku 📱</li>
                  <li>2. Zkopírujte 6-místný kód</li>
                  <li>3. Zadejte kód do pole výše</li>
                  <li>4. Klikněte na "Spárovat"</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog pro potvrzení odpojení */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 15000 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              ⚠️ Odpojit synchronizaci?
            </h2>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Opravdu chcete odpojit {IS_MOBILE ? 'telefon od PC' : 'PC od telefonu'}? Synchronizace bude ukončena a kontakty se přestanou zobrazovat.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="flex-1 py-3 px-4 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors"
              >
                Zrušit
              </button>
              <button
                onClick={confirmDisconnect}
                className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors"
              >
                Odpojit
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Zobrazení kontaktů na mobilu */}
      {IS_MOBILE && currentCompany && (
        <>
          {/* Rozmazané pozadí */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" 
            style={{ zIndex: 9998 }} 
            onClick={() => setCurrentCompany(null)} 
          />
          
          {/* Karta s kontakty */}
          <div 
            className="fixed left-4 right-4 bg-white dark:bg-gray-800 shadow-2xl p-4 border-red-500 dark:border-red-400" 
            style={{ 
              zIndex: 9999, 
              bottom: 'calc(100px + env(safe-area-inset-bottom))', 
              maxHeight: '60vh', 
              overflowY: 'auto',
              animation: 'borderLightning 2s ease-in-out infinite',
              borderRadius: '24px',
              borderWidth: '3px'
            }}
          >
            {/* Hlavička - jméno a IČO na jednom řádku */}
            <div className="mb-4 text-center">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  📱 {currentCompany.name}
                </h3>
                {currentCompany.ico && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-semibold">
                    IČO: {currentCompany.ico}
                  </span>
                )}
              </div>
            </div>

          {currentCompany.contacts && currentCompany.contacts.length > 0 ? (
            <div className="space-y-3">
              {currentCompany.contacts.map((contact, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4" style={{ borderRadius: '20px' }}>
                  {/* Jméno a telefon na jednom řádku, centrované */}
                  <div className="text-center mb-3">
                    <div className="font-bold text-lg text-gray-900 dark:text-white">
                      {contact.name || 'Bez jména'}
                    </div>
                    {contact.phone && (
                      <div className="text-base text-gray-600 dark:text-gray-300 font-semibold mt-1">
                        {contact.phone}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white py-4 px-4 font-bold text-lg shadow-lg"
                        style={{ borderRadius: '16px' }}
                      >
                        <Phone className="w-6 h-6" />
                        Zavolat
                      </a>
                    )}
                    {contact.phone && (
                      <a
                        href={`sms:${contact.phone}`}
                        className="flex items-center justify-center gap-3 bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-white py-4 px-4 font-bold text-lg shadow-lg"
                        style={{ borderRadius: '16px' }}
                      >
                        <MessageSquare className="w-6 h-6" />
                        Napsat SMS
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Žádné kontakty k zobrazení
            </p>
          )}
        </div>
        </>
      )}
    </>
  );
};

export default memo(PhoneSync, (prev, next) =>
  prev.userId === next.userId &&
  prev.currentCompanyId === next.currentCompanyId &&
  prev.hideIcon === next.hideIcon
);
